import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import DataTable from '../../components/DataTable';
import DialogShell from '../../components/DialogShell';
import {
  listProviders,
  testProvider,
  resetProvider,
  getProvidersStats,
  createResetLastDieRequest,
  cancelResetLastDieRequest,
  confirmResetLastDieRequest,
} from '../../services/cloneProvider';
import {
  LiveDot,
  NeedReloginBadge,
  getErrorMessage,
  formatDateTime,
  ProviderStatsByServing,
  PROVIDER_STATE_ORDER,
} from './badges';
import useUrlState, { useUrlStateBatch } from './useUrlState';

const SERVICE_TYPES = ['fb_ql', 'fb_eaaaau', 'fb_eaagno'];
const PAGE_SIZE = 50;

function ProviderStatsBars({ providersStats }) {
  if (!providersStats) return null;
  return (
    <div className="rounded-xl border border-cyan-100 bg-white p-3">
      <ProviderStatsByServing providersStats={providersStats} />
    </div>
  );
}

function ResetLastDieDialog({ resetToken, count, providersStats, onClose, onDone }) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  async function handleCancel() {
    setIsCancelling(true);
    try {
      await cancelResetLastDieRequest(resetToken);
    } catch (error) {
      // Lỗi mạng lúc cancel không sao - request kẹt ở RAM backend sẽ tự
      // hết hạn (DELETE_REQUEST_TIMEOUT_SECONDS), không có gì bị reset vì
      // chưa confirm. Vẫn đóng dialog bình thường.
    } finally {
      setIsCancelling(false);
      onClose();
    }
  }

  async function handleConfirm() {
    setIsConfirming(true);
    try {
      const data = await confirmResetLastDieRequest(resetToken);
      toast.success(`Đã reset last_die cho ${data.count} provider.`);
      onDone();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsConfirming(false);
    }
  }

  return (
    <DialogShell onClose={handleCancel}>
      <div className="max-h-[80vh] w-[70vw] max-w-full overflow-y-auto">
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Xác nhận reset last_die</h3>
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
          Sẽ reset last_die cho <span className="font-bold">{count}</span> provider khớp bộ lọc hiện tại.
        </div>

        <ProviderStatsBars providersStats={providersStats} />

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isCancelling || isConfirming}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Huỷ
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isCancelling || isConfirming}
            className="flex-1 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isConfirming ? 'Đang reset...' : 'Xác nhận Reset'}
          </button>
        </div>
      </div>
    </DialogShell>
  );
}

export default function ProvidersTab({ initialFilters }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageStr, setPageStr] = useUrlState('providers', 'page', '1');
  const page = Number(pageStr);
  const setPage = useCallback(
    (next) => setPageStr((prev) => String(typeof next === 'function' ? next(Number(prev)) : next)),
    [setPageStr]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showStatsBars, setShowStatsBars] = useState(true);
  const defaultServiceType = initialFilters?.service_type || '';
  const [serviceType] = useUrlState('providers', 'service_type', defaultServiceType);
  const [isLive] = useUrlState('providers', 'is_live', '');
  const [state] = useUrlState('providers', 'state', '');
  const [isServing] = useUrlState('providers', 'is_serving', '');
  const [onlyServingAll] = useUrlState('providers', 'only_serving_all', '');
  const [accountUid] = useUrlState('providers', 'account_uid', '');
  const [accountAddedAtPrefix] = useUrlState('providers', 'account_added_at_prefix', '');
  const filters = {
    service_type: serviceType,
    is_live: isLive,
    state,
    is_serving: isServing,
    only_serving_all: onlyServingAll,
    account_uid: accountUid,
    account_added_at_prefix: accountAddedAtPrefix,
  };
  // 2 ô text không áp dụng theo từng phím gõ (mỗi lần áp dụng là 1 request) -
  // gõ vào draft, Enter/nút Lọc mới đẩy lên filters (URL).
  const [textDraft, setTextDraft] = useState({
    account_uid: accountUid,
    account_added_at_prefix: accountAddedAtPrefix,
  });
  // Đổi filter + reset page về 1 trong đúng 1 lần cập nhật URL - xem
  // useUrlStateBatch.
  const setUrlParams = useUrlStateBatch('providers');
  function setFilters(next) {
    const resolved = typeof next === 'function' ? next(filters) : next;
    setUrlParams({ ...resolved, page: 1 }, { page: '1', service_type: defaultServiceType });
  }
  const [sortBy, setSortBy] = useUrlState('providers', 'sort_by', '');
  const [sortAscStr, setSortAscStr] = useUrlState('providers', 'sort_asc', 'true');
  const sortAsc = sortAscStr === 'true';
  const setSortAsc = (next) => setSortAscStr(String(typeof next === 'function' ? next(sortAsc) : next));
  const [busyId, setBusyId] = useState(null);
  const [stats, setStats] = useState(null);
  const [resetLastDieRequest, setResetLastDieRequest] = useState(null);
  const [isCreatingResetRequest, setIsCreatingResetRequest] = useState(false);

  useEffect(() => {
    getProvidersStats().then(setStats).catch((error) => toast.error(getErrorMessage(error)));
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (filters.service_type) params.service_type = filters.service_type;
      if (filters.is_live) params.is_live = filters.is_live === 'true';
      if (filters.state) params.state = filters.state;
      if (filters.is_serving) params.is_serving = filters.is_serving === 'true';
      if (filters.only_serving_all) params.only_serving_all = filters.only_serving_all === 'true';
      if (filters.account_uid) params.account_uid = filters.account_uid;
      if (filters.account_added_at_prefix) params.account_added_at_prefix = filters.account_added_at_prefix;
      if (sortBy) { params.sort_by = sortBy; params.sort_reverse = !sortAsc; }

      const data = await listProviders(params);
      setItems(data.items);
      setTotal(data.paging.total_item);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [page, serviceType, isLive, state, isServing, onlyServingAll, accountUid, accountAddedAtPrefix, sortBy, sortAsc]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function handleTest(providerId) {
    setBusyId(providerId);
    try {
      await testProvider(providerId);
      toast.success('Test xong.');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  async function handleReset(providerId) {
    setBusyId(providerId);
    try {
      await resetProvider(providerId);
      toast.success('Reset xong.');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  async function handleCheckAndResetLastDie() {
    setIsCreatingResetRequest(true);
    try {
      const params = {};
      if (filters.service_type) params.service_type = filters.service_type;
      if (filters.is_live) params.is_live = filters.is_live === 'true';
      if (filters.state) params.state = filters.state;
      if (filters.is_serving) params.is_serving = filters.is_serving === 'true';
      if (filters.only_serving_all) params.only_serving_all = filters.only_serving_all === 'true';
      if (filters.account_uid) params.account_uid = filters.account_uid;
      if (filters.account_added_at_prefix) params.account_added_at_prefix = filters.account_added_at_prefix;

      const data = await createResetLastDieRequest(params);
      setResetLastDieRequest(data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreatingResetRequest(false);
    }
  }

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'account_uid', label: 'Account UID' },
    { key: 'service_type', label: 'Service type', sortable: true },
    { key: 'is_live', label: 'Live', sortable: true, render: (row) => <LiveDot isLive={row.is_live} /> },
    {
      key: 'is_serving',
      label: 'Serving',
      render: (row) => (
        <span className={row.is_serving ? 'text-emerald-600' : 'text-slate-400'}>
          {row.is_serving ? 'Yes' : 'No'}
        </span>
      ),
    },
    { key: 'state', label: 'State', sortable: true },
    { key: 'request_counter', label: 'Requests', sortable: true },
    {
      key: 'last_live_at',
      label: 'Last live',
      sortable: true,
      render: (row) => formatDateTime(row.last_live_at),
    },
    {
      key: 'next_request_at',
      label: 'Next request',
      render: (row) => formatDateTime(row.next_request_at),
    },
    { key: 'last_reason', label: 'Last reason', maxWidth: 220, wrap: 'truncate', render: (row) => row.last_reason || '—' },
    { key: 'need_account_relogin', label: 'Relogin', render: (row) => <NeedReloginBadge value={row.need_account_relogin} /> },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busyId === row.id}
            onClick={() => handleTest(row.id)}
            className="text-xs font-medium text-cyan-700 hover:underline disabled:opacity-40"
          >
            Test
          </button>
          <button
            type="button"
            disabled={busyId === row.id}
            onClick={() => handleReset(row.id)}
            className="text-xs font-medium text-cyan-700 hover:underline disabled:opacity-40"
          >
            Reset
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      {stats && (
        <div className="rounded-xl border border-cyan-100 bg-white p-3">
          <label className="mb-2 flex items-center justify-end gap-1.5 text-xs text-slate-500">
            <input type="checkbox" checked={showStatsBars} onChange={(e) => setShowStatsBars(e.target.checked)} />
            Hiện bar
          </label>
          <ProviderStatsByServing
            providersStats={stats}
            onSegmentClick={(serviceType, branch) =>
              setFilters((f) => ({ ...f, service_type: serviceType, only_serving_all: branch === 'all' ? 'true' : 'false' }))
            }
            showBars={showStatsBars}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-cyan-100 bg-white p-3">
        <select
          value={filters.service_type}
          onChange={(e) => setFilters((f) => ({ ...f, service_type: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Service type: Tất cả</option>
          {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filters.is_live}
          onChange={(e) => setFilters((f) => ({ ...f, is_live: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Live: Tất cả</option>
          <option value="true">Live</option>
          <option value="false">Die</option>
        </select>
        <select
          value={filters.state}
          onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">State: Tất cả</option>
          {PROVIDER_STATE_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filters.is_serving}
          onChange={(e) => setFilters((f) => ({ ...f, is_serving: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">Serving: Tất cả</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
        <select
          value={filters.only_serving_all}
          onChange={(e) => setFilters((f) => ({ ...f, only_serving_all: e.target.value }))}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
        >
          <option value="">All/Only: Tất cả</option>
          <option value="true">All</option>
          <option value="false">Only</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Account UID
          <input
            type="text"
            placeholder="account uid"
            value={textDraft.account_uid}
            onChange={(e) => setTextDraft((d) => ({ ...d, account_uid: e.target.value.trim() }))}
            onKeyDown={(e) => { if (e.key === 'Enter') setFilters((f) => ({ ...f, ...textDraft })); }}
            className="w-44 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Account added at
          <input
            type="text"
            placeholder="2026-10-05 07:50:17"
            value={textDraft.account_added_at_prefix}
            onChange={(e) => setTextDraft((d) => ({ ...d, account_added_at_prefix: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') setFilters((f) => ({ ...f, ...textDraft })); }}
            className="w-48 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-mono"
          />
        </label>
        <button
          type="button"
          onClick={() => setFilters((f) => ({ ...f, ...textDraft }))}
          className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          Lọc
        </button>
        <button
          type="button"
          onClick={handleCheckAndResetLastDie}
          disabled={isCreatingResetRequest}
          className="ml-auto rounded-lg border border-amber-200 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
        >
          {isCreatingResetRequest ? 'Đang kiểm tra...' : 'Kiểm tra và Reset last_die'}
        </button>
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        sortBy={sortBy}
        sortDir={sortAsc ? 'asc' : 'desc'}
        onSortChange={(key, dir) => { setSortBy(key); setSortAsc(dir === 'asc'); }}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {resetLastDieRequest && (
        <ResetLastDieDialog
          resetToken={resetLastDieRequest.reset_token}
          count={resetLastDieRequest.count}
          providersStats={resetLastDieRequest.providers_stats}
          onClose={() => setResetLastDieRequest(null)}
          onDone={() => {
            setResetLastDieRequest(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
